import * as React from 'react'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Button } from './button'
import { Input } from './input'
import { cn } from '@/lib/utils'

export interface FilterSearchMultiSelectOption {
  value: string
  label: string
}

interface FilterSearchMultiSelectProps {
  options: FilterSearchMultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  onSearchChange: (search: string) => void
  searchValue: string
  searchPlaceholder?: string
  placeholder: string
  icon?: React.ReactNode
  isLoading?: boolean
  isError?: boolean
  className?: string
}

export function FilterSearchMultiSelect({
  options,
  selected,
  onChange,
  onSearchChange,
  searchValue,
  searchPlaceholder = 'Search...',
  placeholder,
  icon,
  isLoading,
  isError,
  className,
}: FilterSearchMultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const rootRef = React.useRef<HTMLDivElement>(null)
  const onSearchChangeRef = React.useRef(onSearchChange)
  onSearchChangeRef.current = onSearchChange

  React.useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return

      if (rootRef.current?.contains(target)) return
      if (target.closest('[data-filter-search-multiselect-content]')) return

      setOpen(false)
      onSearchChangeRef.current('')
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
    }
  }, [open])

  const selectedLabels = React.useMemo(() => {
    const labelByValue = new Map(options.map(o => [o.value, o.label]))
    return selected.map(value => labelByValue.get(value) ?? value)
  }, [options, selected])

  const label =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? (selectedLabels[0] ?? placeholder)
        : `${selectedLabels[0] ?? 'Selected'} +${selected.length - 1}`

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  return (
    <div ref={rootRef} className='w-full min-w-0'>
      <Popover
        open={open}
        onOpenChange={nextOpen => {
          setOpen(nextOpen)
          if (!nextOpen) onSearchChange('')
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant='outline'
            disabled={isLoading || isError}
            className={cn(
              'h-9 w-full border-border hover:bg-accent/30 transition-colors text-xs rounded-lg justify-between px-3 font-normal',
              selected.length > 0 && 'border-primary/50 bg-primary/5',
              className
            )}
          >
            <div className='flex items-center gap-2 min-w-0'>
              {icon && (
                <span className='text-muted-foreground shrink-0'>{icon}</span>
              )}
              <span className='truncate text-left'>
                {isLoading ? 'Loading...' : isError ? 'Error' : label}
              </span>
            </div>
            <ChevronDown className='ml-2 h-3.5 w-3.5 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          data-filter-search-multiselect-content=''
          className='w-[var(--radix-popover-trigger-width)] p-0 max-h-[320px] flex flex-col'
          align='start'
          onOpenAutoFocus={event => event.preventDefault()}
          onCloseAutoFocus={event => event.preventDefault()}
        >
          <div className='flex items-center border-b border-border p-2 shrink-0'>
            <div className='flex items-center relative bg-muted rounded-md py-1 flex-1'>
              <Search className='absolute h-3.5 w-3.5 shrink-0 text-muted-foreground ms-2' />
              <Input
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={e => onSearchChange(e.target.value)}
                className='h-7 w-full border-none text-xs ps-7'
                autoFocus
              />
            </div>
            {searchValue && (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-6 w-6 hover:bg-muted'
                onClick={() => onSearchChange('')}
              >
                <X className='h-3 w-3' />
              </Button>
            )}
          </div>
          <div className='overflow-y-auto p-1 flex-1'>
            {selected.length > 0 && (
              <button
                type='button'
                onClick={() => onChange([])}
                className='w-full flex items-center px-3 py-2 text-xs rounded-md hover:bg-accent transition-colors text-muted-foreground'
              >
                Clear selection
              </button>
            )}
            {isLoading ? (
              <div className='py-4 text-center text-xs text-muted-foreground'>
                Loading...
              </div>
            ) : options.length === 0 ? (
              <div className='py-4 text-center text-xs text-muted-foreground'>
                No options available
              </div>
            ) : (
              options.map(option => (
                <button
                  type='button'
                  key={option.value}
                  onClick={() => toggle(option.value)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-xs rounded-md hover:bg-accent transition-colors text-left',
                    selected.includes(option.value) &&
                      'bg-accent/50 text-primary font-medium'
                  )}
                >
                  <div
                    className={cn(
                      'w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0',
                      selected.includes(option.value)
                        ? 'bg-primary border-primary'
                        : 'border-muted-foreground/30'
                    )}
                  >
                    {selected.includes(option.value) && (
                      <Check className='w-2.5 h-2.5 text-primary-foreground stroke-[3]' />
                    )}
                  </div>
                  <span className='truncate'>{option.label}</span>
                </button>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
